import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Check,
  CircleX,
  CreditCard,
  Gift,
  Globe,
  Leaf,
  Lightbulb,
  Package,
  Phone,
  RefreshCw,
  Save,
  Shield,
  ShoppingBag,
  Wrench,
} from 'lucide-react';
import './OrderFailure.css';

const sectionIconProps = { size: 18, strokeWidth: 1.75 };
const failureIconProps = { size: 72, strokeWidth: 1.5 };
const errorStepIconProps = { size: 22, strokeWidth: 1.75 };
const cartItemIconProps = { size: 28, strokeWidth: 1.75 };
const helpIconProps = { size: 18, strokeWidth: 1.75 };
const statusIconProps = { size: 14, strokeWidth: 2.25 };

const OrderFailure = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Add interactive animations
    const failureTitle = document.querySelector('.oss-failure-title');
    const failureMessages = document.querySelectorAll('.oss-failure-message');
    const orderReference = document.querySelector('.oss-order-reference');

    setTimeout(() => {
      if (failureTitle) {
        failureTitle.style.animation = 'oss-slideInUp 0.6s ease-out';
      }
    }, 400);

    setTimeout(() => {
      failureMessages.forEach((msg, index) => {
        msg.style.animation = `oss-slideInUp 0.6s ease-out ${index * 0.1}s`;
      });
    }, 600);

    setTimeout(() => {
      if (orderReference) {
        orderReference.style.animation = 'oss-slideInUp 0.6s ease-out';
      }
    }, 800);

    // Add hover effects to cards
    const cards = document.querySelectorAll('.oss-info-card, .oss-cart-details, .oss-help-summary, .oss-troubleshoot');
    const handleMouseEnter = function() {
      this.style.transform = 'translateY(-2px)';
      this.style.transition = 'transform 0.2s ease';
    };
    
    const handleMouseLeave = function() {
      this.style.transform = 'translateY(0)';
    };

    cards.forEach(card => {
      card.addEventListener('mouseenter', handleMouseEnter);
      card.addEventListener('mouseleave', handleMouseLeave);
    });

    // Cleanup event listeners
    return () => {
      cards.forEach(card => {
        card.removeEventListener('mouseenter', handleMouseEnter);
        card.removeEventListener('mouseleave', handleMouseLeave);
      });
    };
  }, []);

  return (
    <div className="oss-body">
      <div className="oss-main-container">
        {/* Failure Banner */}
        <div className="oss-failure-banner">
          <div className="oss-failure-icon" aria-hidden>
            <CircleX {...failureIconProps} />
          </div>
          <h1 className="oss-failure-title">Order Processing Failed</h1>
          <p className="oss-failure-message">We're sorry, but we couldn't process your order at this time.</p>
          <p className="oss-failure-message">Don't worry - your payment was not charged and your items are still in your cart.</p>
          <div className="oss-order-reference">Reference #EB-FAIL-001923</div>
        </div>

        {/* Troubleshooting Steps */}
        <div className="oss-troubleshoot">
          <div className="oss-section-header">
            <h3 className="oss-section-title">
              <Wrench {...sectionIconProps} className="oss-section-title-icon" aria-hidden />
              What Happened?
            </h3>
          </div>
          <div className="oss-troubleshoot-content">
            <div className="oss-error-steps">
              <div className="oss-error-step">
                <div className="oss-error-icon" aria-hidden>
                  <CreditCard {...errorStepIconProps} />
                </div>
                <div className="oss-error-details">
                  <h4 className="oss-error-title">Payment Authorization</h4>
                  <p className="oss-error-description">Your payment method couldn't be authorized. This could be due to insufficient funds, expired card, or bank security measures.</p>
                </div>
              </div>
              
              <div className="oss-error-step">
                <div className="oss-error-icon" aria-hidden>
                  <Package {...errorStepIconProps} />
                </div>
                <div className="oss-error-details">
                  <h4 className="oss-error-title">Inventory Check</h4>
                  <p className="oss-error-description">Some items in your cart may no longer be available or have limited stock.</p>
                </div>
              </div>
              
              <div className="oss-error-step">
                <div className="oss-error-icon" aria-hidden>
                  <Globe {...errorStepIconProps} />
                </div>
                <div className="oss-error-details">
                  <h4 className="oss-error-title">Technical Issue</h4>
                  <p className="oss-error-description">A temporary system error occurred during processing. Our team has been notified.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="oss-content-grid">
          {/* Cart Items */}
          <div className="oss-cart-details">
            <div className="oss-section-header">
              <h3 className="oss-section-title">
                <ShoppingBag {...sectionIconProps} className="oss-section-title-icon" aria-hidden />
                Items in Your Cart
              </h3>
            </div>
            <div className="oss-section-content">
              <div className="oss-cart-item">
                <div className="oss-item-image" aria-hidden>
                  <Package {...cartItemIconProps} />
                </div>
                <div className="oss-item-details">
                  <h4 className="oss-item-name">Premium Kraft Paper Bags</h4>
                  <p className="oss-item-specs">25cm x 32cm • 120g GSM • Kraft Paper</p>
                  <p className="oss-item-quantity">Quantity: 2</p>
                  <span className="oss-item-status oss-status-available">
                    <Check {...statusIconProps} className="oss-status-icon" aria-hidden />
                    Available
                  </span>
                </div>
                <div className="oss-item-price">$45.98</div>
              </div>

              <div className="oss-cart-item">
                <div className="oss-item-image" aria-hidden>
                  <Gift {...cartItemIconProps} />
                </div>
                <div className="oss-item-details">
                  <h4 className="oss-item-name">Luxury Gift Paper Bags</h4>
                  <p className="oss-item-specs">20cm x 28cm • 150g GSM • Coated Paper</p>
                  <p className="oss-item-quantity">Quantity: 1</p>
                  <span className="oss-item-status oss-status-limited">
                    <AlertTriangle {...statusIconProps} className="oss-status-icon" aria-hidden />
                    Limited Stock
                  </span>
                </div>
                <div className="oss-item-price">$32.50</div>
              </div>

              <div className="oss-cart-item">
                <div className="oss-item-image" aria-hidden>
                  <Leaf {...cartItemIconProps} />
                </div>
                <div className="oss-item-details">
                  <h4 className="oss-item-name">Eco-Friendly Brown Paper Bags</h4>
                  <p className="oss-item-specs">30cm x 40cm • 90g GSM • Recycled Paper</p>
                  <p className="oss-item-quantity">Quantity: 1</p>
                  <span className="oss-item-status oss-status-available">
                    <Check {...statusIconProps} className="oss-status-icon" aria-hidden />
                    Available
                  </span>
                </div>
                <div className="oss-item-price">$78.99</div>
              </div>
            </div>
          </div>

          {/* Help Summary */}
          <div className="oss-help-summary">
            <div className="oss-section-header">
              <h3 className="oss-section-title">
                <Lightbulb {...sectionIconProps} className="oss-section-title-icon" aria-hidden />
                Next Steps
              </h3>
            </div>
            <div className="oss-help-content">
              <div className="oss-help-item">
                <div className="oss-help-icon" aria-hidden>
                  <RefreshCw {...helpIconProps} />
                </div>
                <div className="oss-help-text">
                  <h4 className="oss-help-title">Try Again</h4>
                  <p className="oss-help-description">Click "Retry Order" to attempt processing again</p>
                </div>
              </div>

              <div className="oss-help-item">
                <div className="oss-help-icon" aria-hidden>
                  <CreditCard {...helpIconProps} />
                </div>
                <div className="oss-help-text">
                  <h4 className="oss-help-title">Check Payment</h4>
                  <p className="oss-help-description">Verify your payment method details and try a different card</p>
                </div>
              </div>

              <div className="oss-help-item">
                <div className="oss-help-icon" aria-hidden>
                  <Phone {...helpIconProps} />
                </div>
                <div className="oss-help-text">
                  <h4 className="oss-help-title">Contact Support</h4>
                  <p className="oss-help-description">Our team is here to help resolve any issues</p>
                </div>
              </div>

              <div className="oss-help-item">
                <div className="oss-help-icon" aria-hidden>
                  <Save {...helpIconProps} />
                </div>
                <div className="oss-help-text">
                  <h4 className="oss-help-title">Save for Later</h4>
                  <p className="oss-help-description">Items will remain in your cart for 24 hours</p>
                </div>
              </div>
            </div>

            <div className="oss-summary-total">
              <div className="oss-summary-row">
                <span className="oss-total-label">Cart Total</span>
                <span className="oss-total-value">$175.73</span>
              </div>
              <p className="oss-payment-note">No payment was processed</p>
            </div>
          </div>
        </div>

        {/* Contact & Support Info */}
        <div className="oss-info-grid">
          <div className="oss-info-card">
            <div className="oss-section-header">
              <h3 className="oss-section-title">
                <Phone {...sectionIconProps} className="oss-section-title-icon" aria-hidden />
                Get Help
              </h3>
            </div>
            <div className="oss-info-content">
              <div className="oss-info-item">
                <div className="oss-info-label">Customer Support</div>
                <div className="oss-info-value">
                  Phone: 1-800-ECO-BAGS<br />
                  Email: help@ecobags.com<br />
                  Live Chat: Available 24/7
                </div>
              </div>
              <div className="oss-info-item">
                <div className="oss-info-label">Average Response Time</div>
                <div className="oss-info-value">Within 2 hours</div>
              </div>
            </div>
          </div>

          <div className="oss-info-card">
            <div className="oss-section-header">
              <h3 className="oss-section-title">
                <Shield {...sectionIconProps} className="oss-section-title-icon" aria-hidden />
                Your Account
              </h3>
            </div>
            <div className="oss-info-content">
              <div className="oss-info-item">
                <div className="oss-info-label">Account</div>
                <div className="oss-info-value">john.doe@email.com</div>
              </div>
              <div className="oss-info-item">
                <div className="oss-info-label">Cart Status</div>
                <div className="oss-info-value">Items saved automatically</div>
              </div>
              <div className="oss-info-item">
                <div className="oss-info-label">Payment Status</div>
                <div className="oss-info-value">No charges applied</div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="oss-action-buttons">
          <button type="button" className="oss-action-btn oss-primary-btn" onClick={() => navigate('/payment')}>
            Retry Order
          </button>
          <button type="button" className="oss-action-btn oss-secondary-btn" onClick={() => navigate('/payment')}>
            Update Payment Method
          </button>
          <a href="mailto:support@srishiv.com" className="oss-action-btn oss-outline-btn">
            Contact Support
          </a>
          <button type="button" className="oss-action-btn oss-outline-btn" onClick={() => navigate('/')}>
            Continue Shopping
          </button>
        </div>

        {/* Additional Help */}
        <div className="oss-additional-help">
          <h3>Common Solutions</h3>
          <div className="oss-solution-grid">
            <div className="oss-solution-item">
              <strong>Payment Declined?</strong>
              <p>Contact your bank or try a different payment method. Some banks block online transactions for security.</p>
            </div>
            <div className="oss-solution-item">
              <strong>Items Out of Stock?</strong>
              <p>Sign up for restock notifications or check our similar products section for alternatives.</p>
            </div>
            <div className="oss-solution-item">
              <strong>Technical Issues?</strong>
              <p>Clear your browser cache, disable ad blockers, or try a different browser.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderFailure;